/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable perfectionist/sort-interfaces */
/* eslint-disable @typescript-eslint/ban-types */

import type {
  HttpMethod,
  MediaType,
  PathsWithMethod,
  RequiredKeysOf,
  ResponseObjectMap,
  SuccessResponse,
} from "openapi-typescript-helpers";

import { context, propagation } from "@opentelemetry/api";
import createClient, {
  type HeadersOptions,
  type MaybeOptionalInit,
  type Middleware,
  type ParseAsResponse,
} from "openapi-fetch";

import { getEnv } from "@/shared.utils/env";

import { HttpError } from "./exception";
import { type paths } from "./schema";

/**
 * Config.
 * ---
 * We'll use middleware to throw error if response status is not 2xx
 * because it's behavior tanstack-react-query expects.
 *
 * And since we change behavior, data will always exists if function not throw error.
 * So we need these type below to make type of data not undefined.
 */

// Copied from `openapi-fetch/dist/index.d.ts`
type InitParam<Init> = RequiredKeysOf<Init> extends never
  ? [({ [key: string]: unknown } & Init)?]
  : [{ [key: string]: unknown } & Init];

// Only change to type is the `FetchResponse` by removing `data?: never` part.
// Other than that just copied from `openapi-fetch/dist/index.d.ts`
type FetchResponse<T extends Record<number | string, any>, Options, Media extends MediaType> =
  {
    data: ParseAsResponse<SuccessResponse<ResponseObjectMap<T>, Media>, Options>;
    response: Response;
  };

export type ClientMethod<
  Paths extends Record<string, Record<HttpMethod, {}>>,
  Method extends HttpMethod,
  Media extends MediaType,
> = <Path extends PathsWithMethod<Paths, Method>, Init extends MaybeOptionalInit<Paths[Path], Method>>(
  url: Path,
  ...init: InitParam<Init>
) => Promise<FetchResponse<Paths[Path][Method], Init, Media>>;

interface Client<Paths extends {}, Media extends MediaType = MediaType> {
  /** Call a GET endpoint */
  GET: ClientMethod<Paths, "get", Media>;
  /** Call a PUT endpoint */
  PUT: ClientMethod<Paths, "put", Media>;
  /** Call a POST endpoint */
  POST: ClientMethod<Paths, "post", Media>;
  /** Call a DELETE endpoint */
  DELETE: ClientMethod<Paths, "delete", Media>;
  /** Call a OPTIONS endpoint */
  OPTIONS: ClientMethod<Paths, "options", Media>;
  /** Call a HEAD endpoint */
  HEAD: ClientMethod<Paths, "head", Media>;
  /** Call a PATCH endpoint */
  PATCH: ClientMethod<Paths, "patch", Media>;
  /** Call a TRACE endpoint */
  TRACE: ClientMethod<Paths, "trace", Media>;
  /** Register middleware */
  use(...middleware: Middleware[]): void;
  /** Unregister middleware */
  eject(...middleware: Middleware[]): void;
}

const clientPanich = createClient({
  baseUrl: getEnv(
    process.env.NEXT_PUBLIC_PANICH_API,
    "NEXT_PUBLIC_PANICH_API",
  ),
}) as Client<paths>;

const tanStackOnResponseMiddleware: NonNullable<Middleware["onResponse"]> = async ({ request, response, options }) => {
  // TanStack Query expects error to be thrown.
  // """A query function can be literally any function that returns a promise.
  // The promise that is returned should either resolve the data or throw an error."""
  if (!response.ok) {
    let message: string | undefined;

    const raw = await response.text();

    const fallbackErrorMessage = `${response.status} - ${request.url}`;

    try {
      const r = JSON.parse(raw);
      message = typeof r?.message === "string" ? r.message : undefined;
    }
    catch {
      message = undefined;
    }

    const errorMessage = message || fallbackErrorMessage;

    throw new HttpError({
      statusCode: response.status,
      message: errorMessage,
      errors: {
        url: request.url,
        method: request.method,
      },
    });
  }
};

const attatchOtelOnRequestMiddleware: NonNullable<Middleware["onRequest"]> = ({ request }) => {
  // Inject OTEL context into request headers
  const carrier: Record<string, string> = {};
  propagation.inject(context.active(), carrier);
  for (const [k, v] of Object.entries(carrier)) {
    request.headers.set(k, v);
  }

  return request;
};

clientPanich.use({
  onRequest: attatchOtelOnRequestMiddleware,
  onResponse: tanStackOnResponseMiddleware,
});

// Request wrapper type
type RequestOptions = {
  headers?: HeadersOptions;
};

export {
  type Client,
  type RequestOptions,
  attatchOtelOnRequestMiddleware,
  clientPanich,
  type paths,
  tanStackOnResponseMiddleware,
};
